import { RecipientInfoDto } from 'app/models/api-dto.model';
import { SavedReport } from 'app/report-wizard/report-wizard.model';
import { Report, ReportType } from 'shared/report';
import { CreateReportDto, ReportDetails } from 'shared/report-model';
import { ElementType } from 'shared/report-template/report-template-adapter/model';

import { AnalyticsTrackEvent, TrackEventContext, TrackEventType } from './track-events.model';
import { TrackEventsService } from './track-events.service';

export class TrackEventsHelper {
    static readonly trackEventsService: TrackEventsService;

    static numberOfUsersAdded(
        trackEventType: AnalyticsTrackEvent,
        usersOnInit: RecipientInfoDto[],
        usersOnSave: RecipientInfoDto[],
        reportId: string,
        siteId: string,
        siteName: string,
    ): TrackEventType {
        const addedRecipients = this.addedOrRemovedRecipients(usersOnInit, usersOnSave);

        if (addedRecipients > 0) {
            return {
                type: trackEventType,
                context: {
                    siteId: siteId,
                    siteName: siteName,
                    reportId: reportId,
                },
                properties: {
                    reports_number_of_added_recipients: addedRecipients,
                    reports_number_of_total_recipients: usersOnSave.length,
                },
            };
        }
        return undefined;
    }

    static numberOfUsersRemoved(
        trackEventType: AnalyticsTrackEvent,
        usersOnInit: RecipientInfoDto[],
        usersOnSave: RecipientInfoDto[],
        reportId: string,
        siteId: string,
        siteName: string,
    ): TrackEventType {
        const removedRecipients = this.addedOrRemovedRecipients(usersOnInit, usersOnSave);

        if (removedRecipients > 0) {
            return {
                type: trackEventType,
                context: {
                    siteId: siteId,
                    siteName: siteName,
                    reportId: reportId,
                },
                properties: {
                    reports_number_of_removed_recipients: removedRecipients,
                },
            };
        }
        return undefined;
    }

    private static addedOrRemovedRecipients(
        usersOnInit: RecipientInfoDto[],
        usersOnSave: RecipientInfoDto[],
    ): number {
        let addedOrRemoved = 0;

        usersOnSave.forEach((userOnSave) => {
            if (
                !usersOnInit.some(
                    (userOnInit) =>
                        userOnInit.userId === userOnSave.userId &&
                        userOnInit.emailAddress === userOnSave.emailAddress,
                )
            ) {
                addedOrRemoved++;
            }
        });

        return addedOrRemoved;
    }

    static splitEmailAndCsvUsers(
        type: ShareUserTrackType,
        report: Pick<SavedReport, 'id' | 'shareList'>,
        shareListOnSave: RecipientInfoDto[],
    ) {
        const original = this.splitOriginalAndSavedShareList(type, report, null);
        const saved = this.splitOriginalAndSavedShareList(type, null, shareListOnSave);
        return { original, saved };
    }

    private static splitOriginalAndSavedShareList(
        type: ShareUserTrackType,
        report: Pick<SavedReport, 'id' | 'shareList'>,
        shareListOnSave: RecipientInfoDto[],
    ) {
        const emailUsers: RecipientInfoDto[] = [];
        const csvUsers: RecipientInfoDto[] = [];

        const splitShareList = (users: RecipientInfoDto[]) => {
            if (!users) return;
            users.forEach((user) => {
                if (user.receiveLink && !user.receiveCsv) {
                    emailUsers.push(user);
                } else if (user.receiveCsv && !user.receiveLink) {
                    csvUsers.push(user);
                }
            });
        };

        if (report && type === ShareUserTrackType.editShareList) {
            splitShareList(report.shareList);
        }

        if (
            shareListOnSave &&
            (type === ShareUserTrackType.editShareList || type === ShareUserTrackType.createReport)
        ) {
            splitShareList(shareListOnSave);
        }

        return { emailUsers, csvUsers };
    }

    static positionPercentage(
        percentage: number,
        report: ReportDetails,
        fromDate: Date,
        toDate: Date,
    ): TrackEventType {
        const trackEventConfig: Record<number, AnalyticsTrackEvent> = {
            [50]: AnalyticsTrackEvent.reportEditionViewed50,
            [100]: AnalyticsTrackEvent.reportEditionViewed100,
        };

        return {
            type: trackEventConfig[percentage],
            context: this.getContextFromReport(report),
            properties: {
                reports_edition_from_date: fromDate,
                reports_edition_to_date: toDate,
            },
        };
    }

    static elementAdded(type: number, report: ReportDetails): TrackEventType {
        let trackEvent: AnalyticsTrackEvent;

        switch (type) {
            case ElementType.LineChart:
                trackEvent = AnalyticsTrackEvent.chartElementAdded;
                break;
            case ElementType.Table:
                trackEvent = AnalyticsTrackEvent.tableElementAdded;
                break;
            case ElementType.ConsumptionTable:
                trackEvent = AnalyticsTrackEvent.consumptionTableElementAdded;
                break;
            case ElementType.ScatterPlot:
                trackEvent = AnalyticsTrackEvent.scatterPlotElementAdded;
                break;
            case ElementType.KpiSet:
                trackEvent = AnalyticsTrackEvent.kpiSetElementAdded;
                break;
            default:
                return undefined;
        }

        return {
            type: trackEvent,
            context: this.getContextFromReport(report),
        };
    }

    static elementUpdated(type: number, report: ReportDetails): TrackEventType {
        let trackEvent: AnalyticsTrackEvent;

        switch (type) {
            case ElementType.LineChart:
                trackEvent = AnalyticsTrackEvent.chartElementUpdated;
                break;
            case ElementType.Table:
                trackEvent = AnalyticsTrackEvent.tableElementUpdated;
                break;
            case ElementType.ConsumptionTable:
                trackEvent = AnalyticsTrackEvent.consumptiontableElementUpdated;
                break;
            case ElementType.ScatterPlot:
                trackEvent = AnalyticsTrackEvent.scatterPlotElementUpdated;
                break;
            case ElementType.KpiSet:
                trackEvent = AnalyticsTrackEvent.kpiSetElementUpdated;
                break;
            default:
                return undefined;
        }

        return {
            type: trackEvent,
            context: this.getContextFromReport(report),
        };
    }

    static elementDeleted(type: ElementType, report: ReportDetails): TrackEventType {
        let trackEvent: AnalyticsTrackEvent;

        switch (type) {
            case ElementType.LineChart:
                trackEvent = AnalyticsTrackEvent.chartElementDeleted;
                break;
            case ElementType.Table:
                trackEvent = AnalyticsTrackEvent.tableElementDeleted;
                break;
            case ElementType.ConsumptionTable:
                trackEvent = AnalyticsTrackEvent.consumptionTableElementDeleted;
                break;
            case ElementType.ScatterPlot:
                trackEvent = AnalyticsTrackEvent.scatterPlotElementDeleted;
                break;
            case ElementType.KpiSet:
                trackEvent = AnalyticsTrackEvent.kpiSetElementDeleted;
                break;
            default:
                return undefined;
        }

        return {
            type: trackEvent,
            context: this.getContextFromReport(report),
        };
    }

    static bandwidthAdded(report: ReportDetails): TrackEventType {
        return {
            type: AnalyticsTrackEvent.bandwidthPropertyAdded,
            context: this.getContextFromReport(report),
        };
    }

    static bandwidthDeleted(report: ReportDetails): TrackEventType {
        return {
            type: AnalyticsTrackEvent.bandwidthPropertyDeleted,
            context: this.getContextFromReport(report),
        };
    }

    static reportCreated(reportDto: CreateReportDto): TrackEventType {
        return {
            type: AnalyticsTrackEvent.newReportCreated,
            context: {
                siteId: reportDto.siteAssets[0]?.siteId,
                siteName: reportDto.siteAssets[0]?.siteName,
            },
        };
    }

    static reportActivated(report: Report): TrackEventType {
        let trackEvent: AnalyticsTrackEvent;

        switch (report.recurrency) {
            case ReportType.OneOff:
                trackEvent = AnalyticsTrackEvent.reportPublished;
                break;
            case ReportType.Recurring:
                trackEvent = AnalyticsTrackEvent.reportActivated;
                break;
            default:
                return undefined;
        }

        return {
            type: trackEvent,
            context: {
                siteId: report.siteAssets[0]?.id,
                siteName: report.siteAssets[0]?.name,
                reportId: report.guid,
            },
        };
    }

    static getContextFromReport(report: ReportDetails): TrackEventContext {
        return {
            siteId: report.siteId,
            siteName: report.location,
            reportId: report.id,
        };
    }
}

export enum ShareUserTrackType {
    createReport = 'CREATE',
    editShareList = 'EDIT',
}
